import { DataScreen } from './screens/data/DataScreen';
import { AskRoot } from './components/ask/AskDock';
import { WebMcpBridge } from './components/WebMcpBridge';
import { useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { StoreProvider, useStore, consumePostLoginRedirect } from './store/StoreContext';
import { FeatureGate, FeatureOff } from './components/FeatureGate'
import { FollowingScreen } from './screens/following/FollowingScreen'
import { useFeatureStrict } from './lib/features'
import { AppLayout } from './layouts/AppLayout';

import { StartScreen } from './screens/auth/StartScreen';
import { LoginScreen } from './screens/auth/LoginScreen';
import { RegisterScreen } from './screens/auth/RegisterScreen';
import { ResetPasswordScreen } from './screens/auth/ResetPasswordScreen';
import { OAuthCallbackScreen } from './screens/auth/OAuthCallbackScreen';

import { ChannelsListScreen } from './screens/channels/ChannelsListScreen';
import { ChannelDetailsScreen } from './screens/channels/ChannelDetailsScreen';
import { ChannelManageScreen } from './screens/channels/ChannelManageScreen';
import { ChannelShareScreen } from './screens/channels/ChannelShareScreen';
import { ChannelInvitesScreen } from './screens/channels/ChannelInvitesScreen';
import { PublicChannelViewScreen } from './screens/channels/PublicChannelViewScreen';
import { PublicChannelPageScreen } from './screens/channels/PublicChannelPageScreen';
import { PublicChannelsListScreen } from './screens/channels/PublicChannelsListScreen';
import { PublicReportScreen } from './screens/channels/PublicReportScreen';
import { StatusReportScreen } from './screens/channels/StatusReportScreen';
import { ChannelPickerScreen } from './screens/channels/ChannelPickerScreen';

import { ChatScreen } from './screens/chat/ChatScreen';
import { AddPostScreen } from './screens/chat/AddPostScreen';
import { AiSettingsScreen } from './screens/chat/AiSettingsScreen';
import { InsightsScreen } from './screens/chat/InsightsScreen';

import { TemplatesListScreen } from './screens/templates/TemplatesListScreen';
import { TemplateManageScreen } from './screens/templates/TemplateManageScreen';
import { PublicTemplateScreen } from './screens/templates/PublicTemplateScreen';
import { TemplatePickerScreen } from './screens/templates/TemplatePickerScreen';

import { IntegrationsListScreen } from './screens/integrations/IntegrationsListScreen';
import { ConnectorCatalogScreen } from './screens/integrations/ConnectorCatalogScreen';
import { IntegrationSetupPlaceholderScreen } from './screens/integrations/IntegrationSetupPlaceholderScreen';
import { GitHubSetupScreen } from './screens/integrations/GitHubSetupScreen';
import { SystemSetupScreen } from './screens/integrations/SystemSetupScreen';
import { WebhookSetupScreen } from './screens/integrations/WebhookSetupScreen';
import { SalesforceSetupScreen } from './screens/integrations/SalesforceSetupScreen';
import { SalesforceOAuthCallbackScreen } from './screens/integrations/SalesforceOAuthCallbackScreen';
import { AiIntegrationScreen } from './screens/integrations/AiIntegrationScreen';
import { AutoMappingScreen } from './screens/integrations/AutoMappingScreen';
import { IntegrationDetailScreen } from './screens/integrations/IntegrationDetailScreen';
import { AgentsScreen } from './screens/integrations/AgentsScreen';
import { AskScreen } from './screens/ask/AskScreen';
import { IntegrationRoutes } from './components/integrations/IntegrationBoundary';
import { MappingManageScreen } from './screens/mappings/MappingManageScreen';
import { MappingDetailScreen } from './screens/mappings/MappingDetailScreen';

import { WorkflowListScreen } from './screens/workflow/WorkflowListScreen';
import { WorkflowScreen } from './screens/workflow/WorkflowScreen';

import { SettingsScreen } from './screens/settings/SettingsScreen';
import { ProfileScreen } from './screens/settings/ProfileScreen';
import { AppearanceScreen } from './screens/settings/AppearanceScreen';
import { ConnectionScreen } from './screens/settings/ConnectionScreen';
import { NotificationsScreen } from './screens/settings/NotificationsScreen';
import { SecurityScreen } from './screens/settings/SecurityScreen';
import { DiagnosticsScreen } from './screens/settings/DiagnosticsScreen';
import { StatsScreen } from './screens/settings/StatsScreen';
import { DatabaseScreen } from './screens/settings/DatabaseScreen';
import { PlanScreen } from './screens/settings/PlanScreen';

import { ShowcaseScreen } from './screens/search/ShowcaseScreen';

import { LegalScreen } from './screens/legal/LegalScreen';
import { TermsScreen } from './screens/legal/TermsScreen';
import { PrivacyScreen } from './screens/legal/PrivacyScreen';
import { CookiePolicyScreen } from './screens/legal/CookiePolicyScreen';
import { SubprocessorsScreen } from './screens/legal/SubprocessorsScreen';
import { DpaScreen } from './screens/legal/DpaScreen';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { AppFooter } from './components/AppFooter';
import { NotFoundScreen } from './screens/NotFoundScreen';

// AppFooter's compact fixed strip is site-wide by default, but the public,
// logged-out channel/report screens (/public/*) now carry their own
// richer, in-flow PublicSiteFooter (see those screens) modeled on the
// marketing site's footer - without this, the two would stack/overlap at
// the bottom of the viewport on desktop.
function SiteWideFooter() {
  const location = useLocation();
  if (location.pathname.startsWith('/public/')) return null;
  return <AppFooter fixed />;
}

// following.v1 is dark-launched: ON only once the server reports it true.
function FollowingRoute() {
  return useFeatureStrict('following.v1') ? <FollowingScreen /> : <FeatureOff />
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, authChecked } = useStore();
  // The session lives in an httpOnly cookie now (see StoreContext.tsx's
  // authChecked comment) - isLoggedIn isn't known synchronously at mount
  // the way a localStorage-backed check used to be, so wait for the
  // boot-time check to resolve before deciding whether to redirect. A
  // real logged-in session would otherwise flash straight to /start for
  // a moment on every reload.
  if (!authChecked) return null;
  if (!isLoggedIn) return <Navigate to="/start" replace />;
  return <>{children}</>;
}

function RequireGuest({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, authChecked } = useStore();
  const navigate = useNavigate();
  // Reading + clearing postLoginRedirect is a one-shot side effect, so it
  // can't live in the render body (StrictMode's dev-only double-render
  // would consume it on the discarded first pass, leaving the second pass -
  // the one that's actually committed - with nothing, always landing on
  // /channels). consumedRef survives both invocations of this effect
  // (StrictMode double-invokes effects too, mount->cleanup->mount) so the
  // second invocation is a no-op instead of re-navigating to /channels.
  const consumedRef = useRef(false);
  useEffect(() => {
    if (isLoggedIn && !consumedRef.current) {
      consumedRef.current = true;
      navigate(consumePostLoginRedirect() || '/channels', { replace: true });
    }
  }, [isLoggedIn, navigate]);
  // See RequireAuth's identical comment - same reason to wait here too,
  // otherwise a real session flashes the login form for a moment on
  // every reload before this effect's redirect fires.
  if (!authChecked) return null;
  if (isLoggedIn) return null;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/channels" replace />} />

      {/* Public - no account, no login. Deliberately outside RequireGuest/
          RequireAuth and AppLayout's sidebar chrome. `:tab` (messages/
          charts/reports) and `:mode` ("full") are optional trailing
          segments PublicChannelViewScreen reads/writes so a visitor can
          share a link straight into one tab, or straight into that tab's
          full-screen view (e.g. a chart maximized) - see that screen's
          own comment on buildPublicUrl. */}
      <Route path="/shared/:nickname/:token/:tab?/:mode?" element={<FeatureGate feature="channels.share_links"><PublicChannelViewScreen /></FeatureGate>} />

      {/* Live-showcase homepage's canonical public channel URL - no share
          link, no token, gated purely on isPublic server-side (see
          backend/workflows/channel/public_view.wsl). This is what the
          landing catalog's "Open channel" links to. */}
      <Route path="/public" element={<FeatureGate feature="channels.public_pages"><PublicChannelsListScreen /></FeatureGate>} />
      <Route path="/public/:nickname" element={<FeatureGate feature="channels.public_pages"><PublicChannelPageScreen /></FeatureGate>} />

      {/* A single report's stable permalink (channel/
          public_report_by_number.wsl) - the public, numbered counterpart
          to /public/:nickname's Reports tab (always the latest). See
          PublicReportScreen.tsx's header comment. */}
      <Route path="/public/:nickname/reports/:number" element={<FeatureGate feature="channels.public_pages"><PublicReportScreen /></FeatureGate>} />

      {/* "Zmist runs on Zmist" - see StatusReportScreen.tsx's header
          comment. Same public placement as /shared/... above. */}
      <Route path="/ask" element={<AskScreen />} />
      <Route path="/status" element={<StatusReportScreen />} />

      {/* Legal pages - public regardless of auth state (a logged-in user
          reaches these from the sidebar's "Legal" link; a guest from links
          wherever they're shown), so deliberately outside RequireGuest/
          RequireAuth too. */}
      <Route path="/legal" element={<LegalScreen />} />
      <Route path="/terms" element={<TermsScreen />} />
      <Route path="/privacy" element={<PrivacyScreen />} />
      <Route path="/cookies" element={<CookiePolicyScreen />} />
      <Route path="/subprocessors" element={<SubprocessorsScreen />} />
      <Route path="/dpa" element={<DpaScreen />} />

      <Route path="/start" element={<RequireGuest><StartScreen /></RequireGuest>} />
      <Route path="/login" element={<RequireGuest><LoginScreen /></RequireGuest>} />
      <Route path="/register" element={<RequireGuest><FeatureGate feature="auth.signup"><RegisterScreen /></FeatureGate></RequireGuest>} />
      <Route path="/reset-password" element={<RequireGuest><FeatureGate feature="auth.password_reset"><ResetPasswordScreen /></FeatureGate></RequireGuest>} />
      <Route path="/oauth/callback/:provider" element={<RequireGuest><OAuthCallbackScreen /></RequireGuest>} />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/channels" element={<ChannelsListScreen />} />
        <Route path="/channels/invites" element={<ChannelInvitesScreen />} />
        <Route path="/channels/new" element={<ChannelManageScreen />} />
        <Route path="/channels/pick" element={<ChannelPickerScreen />} />
        <Route path="/channels/:channelId" element={<ChatScreen />} />
        <Route path="/channels/:channelId/details" element={<ChannelDetailsScreen />} />
        <Route path="/channels/:channelId/edit" element={<ChannelManageScreen />} />
        <Route path="/channels/:channelId/share" element={<FeatureGate feature="channels.share_links"><ChannelShareScreen /></FeatureGate>} />
        {/* Posts tab is hidden for now (see types.ts) - route kept so it's a one-line re-enable later. */}
        <Route path="/channels/:channelId/posts/new" element={<AddPostScreen />} />
        <Route path="/channels/:channelId/ai-settings" element={<FeatureGate feature="reports.ai"><AiSettingsScreen /></FeatureGate>} />
        <Route path="/channels/:channelId/insights" element={<FeatureGate feature="analytics.insights"><InsightsScreen /></FeatureGate>} />
        <Route path="/channels/:channelId/workflows" element={<WorkflowListScreen />} />
        <Route path="/channels/:channelId/workflows/new" element={<WorkflowScreen />} />
        <Route path="/channels/:channelId/workflows/:workflowId" element={<WorkflowScreen />} />

        <Route path="/data" element={<DataScreen />} />
        <Route path="/showcase" element={<ShowcaseScreen />} />
        <Route path="/following" element={<FollowingRoute />} />
        <Route path="/search" element={<Navigate to="/showcase" replace />} />

        <Route path="/templates" element={<FeatureGate feature="templates"><TemplatesListScreen /></FeatureGate>} />
        <Route path="/templates/pick" element={<FeatureGate feature="templates"><TemplatePickerScreen /></FeatureGate>} />
        <Route path="/templates/new" element={<FeatureGate feature="templates"><TemplateManageScreen /></FeatureGate>} />
        <Route path="/templates/:templateId/edit" element={<FeatureGate feature="templates"><TemplateManageScreen /></FeatureGate>} />
        <Route path="/templates/:templateId" element={<FeatureGate feature="templates"><PublicTemplateScreen /></FeatureGate>} />

        {/* Every integration screen sits under one error boundary: a crash or
            failed load is shown (with retry) instead of a blank page. */}
        <Route element={<FeatureGate feature="integrations.mappings"><IntegrationRoutes /></FeatureGate>}>
          <Route path="/integrations" element={<IntegrationsListScreen />} />
          <Route path="/integrations/catalog" element={<ConnectorCatalogScreen />} />
          <Route path="/integrations/agents" element={<AgentsScreen />} />
          {/* Slices 5/6: GitHub and generic-webhook get the real guided flows;
              every other connector still lands on the placeholder until its
              own slice ships (static segments outrank the ":connectorId"
              wildcard under react-router's route-ranking, so declaration
              order doesn't matter here, but they're listed first for
              readability). */}
          <Route path="/integrations/new/github" element={<GitHubSetupScreen />} />
          <Route path="/integrations/new/webhook" element={<WebhookSetupScreen />} />
          <Route path="/integrations/new/system" element={<SystemSetupScreen />} />
          <Route path="/integrations/new/salesforce" element={<FeatureGate feature="integrations.salesforce"><SalesforceSetupScreen /></FeatureGate>} />
          <Route path="/integrations/new/auto" element={<FeatureGate feature="integrations.ai_mapping"><AutoMappingScreen /></FeatureGate>} />
<Route path="/integrations/new/ai" element={<FeatureGate feature="integrations.ai_mapping"><AiIntegrationScreen /></FeatureGate>} />
          <Route path="/integrations/new/:connectorId" element={<IntegrationSetupPlaceholderScreen />} />
          {/* A static literal segment outranks the guest-only "/oauth/callback/
              :provider" route above under react-router's route-ranking
              regardless of declaration order (same reasoning as the comment
              above) - this one needs a logged-in mapping owner, not a guest,
              since it completes an existing mapping's connection rather than
              signing in. */}
          <Route path="/oauth/callback/salesforce" element={<FeatureGate feature="integrations.salesforce"><SalesforceOAuthCallbackScreen /></FeatureGate>} />
          {/* Slice 4: the detail shell is a new screen, but "edit the Mapping"
              reuses MappingManageScreen directly (nickname param renamed to
              mappingId so that screen's existing useParams() call needs no
              change) rather than forking a second editor - see
              IntegrationDetailScreen's header comment. */}
          <Route path="/integrations/:integrationId" element={<IntegrationDetailScreen />} />
          <Route path="/integrations/:mappingId/mapping" element={<MappingManageScreen />} />
        </Route>
        {/* Legacy Mappings routes (roadmap #9 Phase 1): /mappings itself is
            superseded by /integrations as the default list, so it redirects;
            new/edit/detail stay reachable directly since this slice doesn't
            replace those screens yet - see docs/CLAUDE_ZMIST_ROADMAP.md #9. */}
        <Route path="/mappings" element={<Navigate to="/integrations" replace />} />
        <Route path="/mappings/new" element={<FeatureGate feature="integrations.mappings"><MappingManageScreen /></FeatureGate>} />
        <Route path="/mappings/:mappingId/edit" element={<FeatureGate feature="integrations.mappings"><MappingManageScreen /></FeatureGate>} />
        <Route path="/mappings/:mappingId" element={<FeatureGate feature="integrations.mappings"><MappingDetailScreen /></FeatureGate>} />

        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/settings/ai" element={<FeatureGate feature="reports.ai"><AiSettingsScreen /></FeatureGate>} />
        <Route path="/settings/profile" element={<ProfileScreen />} />
        <Route path="/settings/appearance" element={<AppearanceScreen />} />
        <Route path="/settings/connection" element={<ConnectionScreen />} />
        <Route path="/settings/notifications" element={<FeatureGate feature="notifications.push"><NotificationsScreen /></FeatureGate>} />
        <Route path="/settings/security" element={<SecurityScreen />} />
        <Route path="/settings/plan" element={<PlanScreen />} />
        <Route path="/settings/stats/database" element={<DatabaseScreen />} />
        <Route path="/settings/database" element={<Navigate to="/settings/stats/database" replace />} />
        <Route path="/settings/stats" element={<StatsScreen />} />
        {/* Diagnostics moved out of Settings to its own top-level route -
            reached from the main-nav "Diagnostics" tab, which AppLayout
            only shows when Settings > Connection has enabled it. The route
            stays mounted regardless so a bookmarked link still works. */}
        <Route path="/diagnostics" element={<DiagnosticsScreen />} />
        {/* Old Settings location - keep redirecting so existing links/
            bookmarks don't 404. */}
        <Route path="/settings/diagnostics" element={<Navigate to="/diagnostics" replace />} />
      </Route>

      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <StoreProvider>
        <BrowserRouter>
          <WebMcpBridge />
          <AppRoutes />
          {/* The persistent Ask Zmist dock: one instance for the whole app (flag ask.builder). */}
          <AskRoot />
          {/* One site-wide footer, fixed to the bottom of the viewport on
              every route (desktop only - see AppFooter), except /public/*
              - see SiteWideFooter above. Each layout pads its bottom to
              clear it. */}
          <SiteWideFooter />
          <CookieConsentBanner />
        </BrowserRouter>
      </StoreProvider>
    </QueryClientProvider>
  );
}
